import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiArrowLeft, FiCreditCard, FiTruck, FiShield, FiGlobe, FiLock, FiCheck } from 'react-icons/fi';
import { useShopping } from '../context/ShoppingContext';
import { auth } from '../utils/firebase';
import apiClient from '../utils/apiClient';
import razorpayPayment from '../utils/razorpayPayment';
import { stripePayment } from '../utils/stripePayment';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { useToast } from '../context/ToastContext';
import LocationPicker from '../components/LocationPicker';
import '../styles/Checkout.css';

// Stripe configuration
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// Checkout Form Component (wrapped with Stripe Elements)
const CheckoutForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const stripe = useStripe();
  const elements = useElements();
  const toast = useToast();
  const { cartItems, getTotalPrice, clearCart } = useShopping();
  const [couponCode, setcouponCode] = useState('');
  const [appliedcoupon, setAppliedcoupon] = useState(location.state?.appliedcoupon || null);
  const [discountAmount, setDiscountAmount] = useState(location.state?.discountAmount || 0);
  const [applyingcoupon, setApplyingcoupon] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('razorpay'); // 'razorpay' or 'stripe' or 'paypal'
  const [currency, setCurrency] = useState('INR'); // 'INR' or 'USD'

  // Customer details
  const [customerDetails, setCustomerDetails] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    country: 'India'
  });

  const [addressConfirmed, setAddressConfirmed] = useState(false);
  const [isEditingAddress, setIsEditingAddress] = useState(true);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [loadingAddresses, setLoadingAddresses] = useState(false);

  // Load user profile and saved addresses if logged in
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setCustomerDetails(prev => ({
          ...prev,
          name: user.displayName || '',
          email: user.email || ''
        }));

        // Load saved addresses
        try {
          setLoadingAddresses(true);
          const idToken = await user.getIdToken();
          const response = await apiClient.get('/addresses', {
            headers: { Authorization: `Bearer ${idToken}` }
          });
          const addresses = response.data || [];
          setSavedAddresses(addresses);

          // Auto-select default address if available and has valid phone
          const defaultAddress = addresses.find(addr => addr.isDefault);
          if (defaultAddress) {
            // Check if default address has a valid phone number
            const cleanedPhone = (defaultAddress.phone || '').replace(/[\s\-]/g, '').replace(/^\+91/, '');
            const mobileRegex = /^[6-9]\d{9}$/;
            const hasValidPhone = mobileRegex.test(cleanedPhone);

            setSelectedAddressId(defaultAddress.id);
            setCustomerDetails(prev => ({
              ...prev,
              fullName: defaultAddress.fullName,
              phone: defaultAddress.phone,
              address: defaultAddress.address,
              city: defaultAddress.city,
              state: defaultAddress.state,
              pincode: defaultAddress.pincode
            }));

            // Load saved exact location for Quick delivery if available
            console.log('📍 Default address loaded:', defaultAddress);
            console.log('📍 Has lat/lng?', { lat: defaultAddress.lat, lng: defaultAddress.lng });
            if (defaultAddress.lat && defaultAddress.lng) {
              console.log('📍 Setting deliveryLocation from saved address');
              setDeliveryLocation({
                lat: defaultAddress.lat,
                lng: defaultAddress.lng,
                address: defaultAddress.address,
                label: defaultAddress.label
              });
            } else {
              console.log('📍 No lat/lng found in saved address');
            }

            // Only auto-confirm if phone is valid
            if (hasValidPhone) {
              setAddressConfirmed(true);
              setIsEditingAddress(false);
            } else {
              // Keep form open so user can enter valid phone
              setAddressConfirmed(false);
              setIsEditingAddress(true);
            }
          }
        } catch (error) {
          console.error('Error loading addresses:', error);
          // Don't show error toast, user can manually enter address
        } finally {
          setLoadingAddresses(false);
        }
      } else {
        // If not logged in, redirect to login
        navigate('/account', { state: { from: '/checkout' } });
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Redirect if cart is empty
  useEffect(() => {
    if (cartItems.length === 0) {
      navigate('/cart');
    }
  }, [cartItems, navigate]);

  const subtotal = getTotalPrice();

  const [deliveryOptions, setDeliveryOptions] = useState(null);
  const [selectedDelivery, setSelectedDelivery] = useState('standard'); // 'quick', 'express' or 'standard'
  const [loadingDelivery, setLoadingDelivery] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [deliveryLocation, setDeliveryLocation] = useState(null); // {lat, lng, address}
  const [showSaveLocationDialog, setShowSaveLocationDialog] = useState(false);
  const [pendingLocation, setPendingLocation] = useState(null); // Location waiting to be saved
  const [customLocationLabel, setCustomLocationLabel] = useState('');

  // Calculate shipping based on selected delivery option (only when delivery options are available)
  const shippingCost = deliveryOptions?.available
    ? (selectedDelivery === 'quick'
      ? deliveryOptions.quick?.charge
      : selectedDelivery === 'express'
        ? deliveryOptions.express?.charge
        : deliveryOptions.standard?.charge) || 0
    : 0; // No shipping cost until delivery options are loaded
  const total = subtotal + shippingCost - discountAmount;

  // Recalculate discount when subtotal changes (if coupon was applied from cart)
  useEffect(() => {
    if (appliedcoupon) {
      if (appliedcoupon.minOrderValue && subtotal < appliedcoupon.minOrderValue) {
        setAppliedcoupon(null);
        setDiscountAmount(0);
        toast.info('Coupon removed - minimum order value not met');
      } else {
        const discount = appliedcoupon.discountType === 'percentage'
          ? (subtotal * appliedcoupon.discountValue) / 100
          : appliedcoupon.discountValue;
        setDiscountAmount(discount);
      }
    }
  }, [subtotal, appliedcoupon]);

  // Fetch delivery options when pincode is available and address is confirmed
  useEffect(() => {
    const fetchDeliveryOptions = async () => {
      const pincode = customerDetails.pincode;
      if (!pincode || pincode.length !== 6) {
        setDeliveryOptions(null);
        return;
      }

      setLoadingDelivery(true);
      try {
        const response = await apiClient.post('/delivery/options', {
          deliveryPincode: pincode,
          weight: 0.5 // Default weight
        });
        setDeliveryOptions(response.data);
        // Default to standard if available
        if (response.data.available && !selectedDelivery) {
          setSelectedDelivery('standard');
        }
      } catch (error) {
        console.error('Failed to fetch delivery options:', error);
        // No fallback - show error
        setDeliveryOptions({
          available: false,
          error: true,
          message: 'Unable to check delivery options. Please try again.'
        });
      } finally {
        setLoadingDelivery(false);
      }
    };

    if (customerDetails.pincode && customerDetails.pincode.length === 6) {
      fetchDeliveryOptions();
    }
  }, [customerDetails.pincode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerDetails({ ...customerDetails, [name]: value });
  };

  // Validate mobile number (Indian format: 10 digits starting with 6-9)
  const validateMobileNumber = (phone) => {
    if (!phone || phone.trim() === '') {
      return 'Mobile number is required for placing an order';
    }
    // Remove any spaces, dashes, or country code prefix
    const cleanedPhone = phone.replace(/[\s\-]/g, '').replace(/^\+91/, '');
    const mobileRegex = /^[6-9]\d{9}$/;
    if (!mobileRegex.test(cleanedPhone)) {
      return 'Please enter a valid 10-digit Indian mobile number';
    }
    return '';
  };

  const handleAddressSubmit = (e) => {
    e.preventDefault();
    if (!customerDetails.name || !customerDetails.address || !customerDetails.city || !customerDetails.pincode) {
      toast.error('Please fill in all required address fields');
      return;
    }

    // Validate mobile number
    const phoneError = validateMobileNumber(customerDetails.phone);
    if (phoneError) {
      toast.error(phoneError);
      return;
    }

    setAddressConfirmed(true);
    setIsEditingAddress(false);
  };

  const handleSelectSavedAddress = (address) => {
    // Validate mobile number before selecting address
    const phoneError = validateMobileNumber(address.phone);
    if (phoneError) {
      toast.error('This address does not have a valid mobile number. Please update the address or enter a new one.');
      return;
    }

    setSelectedAddressId(address.id);
    setCustomerDetails(prev => ({
      ...prev,
      fullName: address.fullName,
      phone: address.phone,
      address: address.address,
      city: address.city,
      state: address.state,
      pincode: address.pincode
    }));

    // Load saved exact location for Quick delivery if available
    if (address.lat && address.lng) {
      setDeliveryLocation({
        lat: address.lat,
        lng: address.lng,
        address: address.address,
        label: address.label
      });
    } else {
      // No saved location - will need to pick one for Quick delivery
      setDeliveryLocation(null);
    }

    setAddressConfirmed(true);
    setIsEditingAddress(false);
  };

  const handleCurrencyChange = (newCurrency) => {
    setCurrency(newCurrency);
    if (newCurrency === 'INR' && paymentMethod === 'paypal') {
      setPaymentMethod('razorpay');
    } else if (newCurrency === 'USD' && paymentMethod === 'razorpay') {
      setPaymentMethod('stripe');
    }
  };

  const handleApplycoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setApplyingcoupon(true);
    try {
      // Send cart items for validation
      const cartInfo = cartItems.map(item => ({
        id: item.id,
        category: item.category || ''
      }));
      const response = await apiClient.post('/coupons/validate', {
        code: couponCode.toUpperCase(),
        orderTotal: subtotal,
        cartItems: cartInfo
      });

      const coupon = response.data;
      setAppliedcoupon(coupon);

      const discount = coupon.discountType === 'percentage'
        ? (subtotal * coupon.discountValue) / 100
        : coupon.discountValue;
      setDiscountAmount(discount);

      const discountText = coupon.discountType === 'percentage'
        ? `${coupon.discountValue}% off`
        : `₹${coupon.discountValue} off`;
      toast.success(`Coupon applied! ${discountText}`);
      setcouponCode('');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Invalid coupon code');
    } finally {
      setApplyingcoupon(false);
    }
  };

  const handleRemovecoupon = () => {
    setAppliedcoupon(null);
    setDiscountAmount(0);
    toast.info('Coupon removed');
  };

  const handlePayment = async () => {
    if (!addressConfirmed) {
      toast.error('Please confirm your delivery address first.');
      return;
    }

    // Validate mobile number before processing payment
    const phoneError = validateMobileNumber(customerDetails.phone);
    if (phoneError) {
      toast.error(phoneError);
      setIsEditingAddress(true);
      setAddressConfirmed(false);
      return;
    }

    setIsProcessing(true);

    try {
      if (paymentMethod === 'razorpay') {
        // Get fresh Firebase token for payment
        const user = auth.currentUser;
        if (!user) {
          toast.error('Please log in to continue');
          setIsProcessing(false);
          return;
        }

        const idToken = await user.getIdToken(true); // true = force refresh

        await razorpayPayment({
          amount: total,
          currency,
          orderId: null,
          cartItems,
          customerDetails,
          deliveryLocation: deliveryLocation,
          deliveryType: selectedDelivery, // 'quick', 'express', or 'standard'
          shippingCost: shippingCost,
          idToken, // Pass Firebase token
          onSuccess: async (response) => {
            try {


              // Clear cart (order creation already cleared it on backend)
              clearCart();

              // Redirect to success page
              // response.orderId comes from the order created by razorpayPayment
              navigate('/order-success', {
                state: {
                  orderId: response.orderId,
                  paymentId: response.razorpay_payment_id,
                  amount: total,
                  currency: 'INR',
                  paymentMethod: 'Razorpay',
                  items: cartItems
                }
              });
            } catch (err) {
              console.error('❌ Error in onSuccess callback:', err);
              toast.error('Error: ' + err.message);
              setIsProcessing(false);
            }
          },
          onError: (error) => {
            const errorMsg = typeof error === 'string' ? error : (error?.message || 'Unknown error');
            toast.error('Payment failed: ' + errorMsg);
            setIsProcessing(false);
          }
        });
      } else if (paymentMethod === 'stripe') {
        if (!stripe || !elements) {
          toast.error('Stripe has not loaded yet. Please try again.');
          setIsProcessing(false);
          return;
        }

        const cardElement = elements.getElement(CardElement);

        await stripePayment({
          stripe,
          cardElement,
          total,
          currency,
          customerDetails,
          deliveryLocation: deliveryLocation,
          cartItems,
          onSuccess: (paymentIntent) => {
            clearCart();
            navigate('/order-success', {
              state: {
                orderId: paymentIntent.id,
                paymentId: paymentIntent.id,
                amount: total,
                currency: currency,
                paymentMethod: 'Stripe',
                items: cartItems
              }
            });
          },
          onFailure: (error) => {
            toast.error('Payment failed: ' + error.message);
            setIsProcessing(false);
          }
        });
      } else if (paymentMethod === 'paypal') {
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('An error occurred during payment processing.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="checkout-page-amazon">
      <div className="checkout-header-simple">
        <div className="logo-container" onClick={() => navigate('/')}>
          <h2>India Crafts Store</h2>
        </div>
        <h1>Checkout</h1>
        <div className="secure-icon"><FiLock /> Secure Checkout</div>
      </div>

      <div className="checkout-main-layout">
        <div className="checkout-steps-container">

          {/* Step 1: Delivery Address */}
          <div className={`checkout-step ${addressConfirmed ? 'completed' : 'active'}`}>
            <div className="step-header">
              <h3>1. Delivery Address</h3>
              {addressConfirmed && !isEditingAddress && (
                <div className="step-summary">
                  <p>{customerDetails.name}, {customerDetails.address}, {customerDetails.city}, {customerDetails.pincode}</p>
                  <button className="change-btn" onClick={() => setIsEditingAddress(true)}>Change</button>
                </div>
              )}
            </div>

            {(!addressConfirmed || isEditingAddress) && (
              <div className="step-content">
                {/* Display saved addresses */}
                {savedAddresses.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ marginBottom: '10px', color: '#333' }}>Select from saved addresses:</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '10px', marginBottom: '15px' }}>
                      {savedAddresses.map(addr => (
                        <div
                          key={addr.id}
                          onClick={() => handleSelectSavedAddress(addr)}
                          style={{
                            border: selectedAddressId === addr.id ? '2px solid #e77600' : '1px solid #ddd',
                            borderRadius: '4px',
                            padding: '12px',
                            cursor: 'pointer',
                            backgroundColor: selectedAddressId === addr.id ? '#fcf5ee' : '#fff',
                            transition: 'all 0.2s'
                          }}
                        >
                          <div style={{ fontWeight: '600', marginBottom: '5px' }}>{addr.label} {addr.isDefault && <span style={{ color: '#c45500', fontSize: '0.85em' }}>(Default)</span>}</div>
                          <div style={{ fontSize: '0.9em', color: '#555', lineHeight: '1.4' }}>
                            <div>{addr.fullName}</div>
                            <div>{addr.address}</div>
                            <div>{addr.city}, {addr.state} {addr.pincode}</div>
                            <div style={{ marginTop: '5px', color: '#888' }}>{addr.phone}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <hr style={{ margin: '15px 0', borderColor: '#e5e5e5' }} />
                    <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '15px' }}>Or enter a new address:</p>
                  </div>
                )}

                <form onSubmit={handleAddressSubmit} className="address-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Full Name</label>
                      <input
                        type="text"
                        name="name"
                        value={customerDetails.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Mobile Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={customerDetails.phone}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Pincode</label>
                    <input
                      type="text"
                      name="pincode"
                      value={customerDetails.pincode}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Flat, House no., Building, Company, Apartment</label>
                    <input
                      type="text"
                      name="address"
                      value={customerDetails.address}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>City</label>
                      <input
                        type="text"
                        name="city"
                        value={customerDetails.city}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>State</label>
                      <input
                        type="text"
                        name="state"
                        value={customerDetails.state}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <button type="submit" className="use-address-btn">Use this address</button>
                </form>
              </div>
            )}
          </div>

          {/* Step 2: Delivery Options */}
          <div className={`checkout-step ${addressConfirmed ? 'active' : 'disabled'}`}>
            <div className="step-header">
              <div className="step-number">2</div>
              <h2>Choose Delivery Speed</h2>
              {!loadingDelivery && deliveryOptions && (
                <span className="step-indicator">
                  <FiCheck /> {selectedDelivery === 'quick' ? 'Quick' : selectedDelivery === 'express' ? 'Express' : 'Standard'} Selected
                </span>
              )}
            </div>

            {addressConfirmed && (
              <div className="step-content delivery-options-content">
                {loadingDelivery ? (
                  <div className="delivery-loading">
                    <div className="loading-spinner"></div>
                    <p>Checking delivery options for your pincode...</p>
                  </div>
                ) : deliveryOptions?.available ? (
                  <div className="delivery-options">
                    {/* Quick Option - Shiprocket Quick (only show if charge < 200) */}
                    {deliveryOptions.quick?.available && deliveryOptions.quick?.charge < 200 && (
                      <div
                        className={`delivery-option ${selectedDelivery === 'quick' ? 'selected' : ''}`}
                        onClick={() => setSelectedDelivery('quick')}
                      >
                        <div className="delivery-radio">
                          <input
                            type="radio"
                            name="delivery"
                            checked={selectedDelivery === 'quick'}
                            onChange={() => setSelectedDelivery('quick')}
                          />
                        </div>
                        <div className="delivery-info">
                          <div className="delivery-title">
                            <span className="quick-badge">QUICK</span>
                            <span className="delivery-price">₹{deliveryOptions.quick.charge}</span>
                          </div>
                          <div className="delivery-date">
                            Get it in <strong>{deliveryOptions.quick.estimatedTime}</strong>
                          </div>
                          <div className="courier-name">On-demand delivery</div>

                          {/* Location picker for Quick delivery */}
                          {selectedDelivery === 'quick' && (
                            <div className="quick-location-section" onClick={(e) => e.stopPropagation()}>
                              {deliveryLocation && deliveryLocation.address ? (
                                <div className="quick-location-card">
                                  <div className="location-header">
                                    <span className="location-badge">
                                      📍 {deliveryLocation.label || 'Delivery Location'}
                                    </span>
                                    <button
                                      type="button"
                                      className="edit-location-link"
                                      onClick={() => setShowLocationPicker(true)}
                                    >
                                      Edit
                                    </button>
                                  </div>
                                  <p className="location-address">
                                    {deliveryLocation.address}
                                  </p>
                                  {/* Show other saved addresses as chips */}
                                  {savedAddresses.filter(a => a.lat && a.lng && a.id !== selectedAddressId).length > 0 && (
                                    <div className="other-addresses">
                                      <span className="other-label">Deliver to:</span>
                                      {savedAddresses
                                        .filter(a => a.lat && a.lng && a.id !== selectedAddressId)
                                        .map(addr => (
                                          <button
                                            key={addr.id}
                                            type="button"
                                            className="address-chip"
                                            onClick={() => {
                                              setDeliveryLocation({
                                                lat: addr.lat,
                                                lng: addr.lng,
                                                address: addr.address,
                                                label: addr.label
                                              });
                                              setSelectedAddressId(addr.id);
                                              setCustomerDetails(prev => ({
                                                ...prev,
                                                fullName: addr.fullName,
                                                phone: addr.phone,
                                                address: addr.address,
                                                city: addr.city,
                                                state: addr.state,
                                                pincode: addr.pincode
                                              }));
                                            }}
                                          >
                                            {addr.label}
                                          </button>
                                        ))
                                      }
                                      <button
                                        type="button"
                                        className="address-chip add-new"
                                        onClick={() => setShowLocationPicker(true)}
                                      >
                                        + New
                                      </button>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="no-location-card">
                                  <p className="no-location-text">📍 Pin your exact delivery location for Quick delivery</p>
                                  <div className="location-options">
                                    {/* Show saved addresses as chips */}
                                    {savedAddresses.filter(a => a.lat && a.lng).length > 0 && (
                                      <div className="saved-address-chips">
                                        {savedAddresses
                                          .filter(a => a.lat && a.lng)
                                          .map(addr => (
                                            <button
                                              key={addr.id}
                                              type="button"
                                              className="address-chip"
                                              onClick={() => {
                                                setDeliveryLocation({
                                                  lat: addr.lat,
                                                  lng: addr.lng,
                                                  address: addr.address,
                                                  label: addr.label
                                                });
                                                handleSelectSavedAddress(addr);
                                              }}
                                            >
                                              📍 {addr.label}
                                            </button>
                                          ))
                                        }
                                      </div>
                                    )}
                                    <button
                                      type="button"
                                      className="pin-location-btn"
                                      onClick={() => setShowLocationPicker(true)}
                                    >
                                      📍 Pin on Map
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Location Picker Modal for Quick Delivery */}
                    {showLocationPicker && (
                      <LocationPicker
                        onLocationSelect={(location) => {
                          setDeliveryLocation(location);
                          setShowLocationPicker(false);
                          // Show save options dialog
                          setPendingLocation(location);
                          setShowSaveLocationDialog(true);
                        }}
                        initialLocation={deliveryLocation}
                        onClose={() => setShowLocationPicker(false)}
                      />
                    )}

                    {/* Save Location Dialog */}
                    {showSaveLocationDialog && pendingLocation && (
                      <div className="save-location-overlay" onClick={() => setShowSaveLocationDialog(false)}>
                        <div className="save-location-dialog" onClick={(e) => e.stopPropagation()}>
                          <h3>Save this location?</h3>
                          <p className="location-preview">
                            {pendingLocation.address?.substring(0, 60)}{pendingLocation.address?.length > 60 ? '...' : ''}
                          </p>

                          <div className="save-options">
                            {/* Existing addresses */}
                            {savedAddresses.map(addr => (
                              <button
                                key={addr.id}
                                type="button"
                                className="save-option-btn"
                                onClick={async () => {
                                  try {
                                    const user = auth.currentUser;
                                    if (user) {
                                      const idToken = await user.getIdToken();
                                      await apiClient.put(`/addresses/${addr.id}`, {
                                        ...addr,
                                        lat: pendingLocation.lat,
                                        lng: pendingLocation.lng,
                                        address: pendingLocation.address
                                      }, {
                                        headers: { Authorization: `Bearer ${idToken}` }
                                      });
                                      setSavedAddresses(prev => prev.map(a =>
                                        a.id === addr.id
                                          ? { ...a, lat: pendingLocation.lat, lng: pendingLocation.lng, address: pendingLocation.address }
                                          : a
                                      ));
                                      toast.success(`Saved as ${addr.label}!`);
                                    }
                                  } catch (error) {
                                    console.error('Failed to save:', error);
                                    toast.error('Failed to save location');
                                  }
                                  setShowSaveLocationDialog(false);
                                  setPendingLocation(null);
                                }}
                              >
                                Save as "{addr.label}"
                              </button>
                            ))}

                            {/* Add new location option */}
                            <div className="new-location-input">
                              <input
                                type="text"
                                placeholder="New label (e.g., Office, Friend)"
                                value={customLocationLabel}
                                onChange={(e) => setCustomLocationLabel(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                              />
                              <button
                                type="button"
                                className="save-option-btn add-new"
                                disabled={!customLocationLabel.trim()}
                                onClick={async () => {
                                  if (!customLocationLabel.trim()) return;
                                  try {
                                    const user = auth.currentUser;
                                    if (user) {
                                      const idToken = await user.getIdToken();
                                      // Create new address with the location
                                      const response = await apiClient.post('/addresses', {
                                        label: customLocationLabel.trim(),
                                        fullName: customerDetails.fullName || customerDetails.name,
                                        phone: customerDetails.phone,
                                        address: pendingLocation.address,
                                        city: customerDetails.city,
                                        state: customerDetails.state,
                                        pincode: customerDetails.pincode,
                                        lat: pendingLocation.lat,
                                        lng: pendingLocation.lng,
                                        isDefault: false
                                      }, {
                                        headers: { Authorization: `Bearer ${idToken}` }
                                      });
                                      setSavedAddresses(prev => [...prev, response.data]);
                                      toast.success(`Added "${customLocationLabel}" location!`);
                                      setCustomLocationLabel('');
                                    }
                                  } catch (error) {
                                    console.error('Failed to create:', error);
                                    toast.error('Failed to save location');
                                  }
                                  setShowSaveLocationDialog(false);
                                  setPendingLocation(null);
                                }}
                              >
                                ➕ Add New
                              </button>
                            </div>

                            {/* Just for this order */}
                            <button
                              type="button"
                              className="save-option-btn skip-btn"
                              onClick={() => {
                                setShowSaveLocationDialog(false);
                                setPendingLocation(null);
                              }}
                            >
                              Just for this order
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Express Option - hide when Quick is showing */}
                    {deliveryOptions.express?.available && !(deliveryOptions.quick?.available && deliveryOptions.quick?.charge < 200) && (
                      <div
                        className={`delivery-option ${selectedDelivery === 'express' ? 'selected' : ''}`}
                        onClick={() => setSelectedDelivery('express')}
                      >
                        <div className="delivery-radio">
                          <input
                            type="radio"
                            name="delivery"
                            checked={selectedDelivery === 'express'}
                            onChange={() => setSelectedDelivery('express')}
                          />
                        </div>
                        <div className="delivery-info">
                          <div className="delivery-title">
                            <span className="express-badge">EXPRESS</span>
                            <span className="delivery-price">₹{deliveryOptions.express.charge}</span>
                          </div>
                          <div className="delivery-date">
                            Get it by <strong>{deliveryOptions.express.estimatedDate || `${deliveryOptions.express.estimatedDays} days`}</strong>
                          </div>
                          {deliveryOptions.express.courierName && (
                            <div className="courier-name">{deliveryOptions.express.courierName}</div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Standard Option */}
                    {deliveryOptions.standard?.available && (
                      <div
                        className={`delivery-option ${selectedDelivery === 'standard' ? 'selected' : ''}`}
                        onClick={() => setSelectedDelivery('standard')}
                      >
                        <div className="delivery-radio">
                          <input
                            type="radio"
                            name="delivery"
                            checked={selectedDelivery === 'standard'}
                            onChange={() => setSelectedDelivery('standard')}
                          />
                        </div>
                        <div className="delivery-info">
                          <div className="delivery-title">
                            <span className="standard-badge">STANDARD</span>
                            <span className="delivery-price">₹{deliveryOptions.standard.charge}</span>
                          </div>
                          <div className="delivery-date">
                            Get it by <strong>{deliveryOptions.standard.estimatedDate || `${deliveryOptions.standard.estimatedDays} days`}</strong>
                          </div>
                          {deliveryOptions.standard.courierName && (
                            <div className="courier-name">{deliveryOptions.standard.courierName}</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : deliveryOptions?.error ? (
                  <div className="delivery-error">
                    <p>⚠️ {deliveryOptions.message || 'Unable to check delivery options.'}</p>
                    <p>Please check your internet connection and try again.</p>
                    <button
                      className="retry-delivery-btn"
                      onClick={() => {
                        const pincode = customerDetails.pincode;
                        setDeliveryOptions(null);
                        // Trigger re-fetch by updating pincode
                        setCustomerDetails(prev => ({ ...prev, pincode: '' }));
                        setTimeout(() => setCustomerDetails(prev => ({ ...prev, pincode })), 100);
                      }}
                    >
                      Retry
                    </button>
                  </div>
                ) : deliveryOptions && !deliveryOptions.available ? (
                  <div className="delivery-not-serviceable">
                    <p>Sorry, we cannot deliver to pincode <strong>{customerDetails.pincode}</strong></p>
                    <p>Please use a different delivery address to proceed with your order.</p>
                  </div>
                ) : (
                  <div className="delivery-unavailable">
                    <p>Enter a valid pincode in the address section to see delivery options</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Step 3: Payment Method */}
          <div className={`checkout-step ${addressConfirmed ? 'active' : 'disabled'}`}>
            <div className="step-header">
              <div className="step-number">3</div>
              <h2>Payment Method</h2>
            </div>

            {addressConfirmed && (
              <div className="step-content">
                <div className="payment-options-list">

                  {/* Razorpay Option */}
                  <div className={`payment-option-radio ${paymentMethod === 'razorpay' ? 'selected' : ''}`}
                    onClick={() => { setPaymentMethod('razorpay'); setCurrency('INR'); }}>
                    <input
                      type="radio"
                      checked={paymentMethod === 'razorpay'}
                      onChange={() => { setPaymentMethod('razorpay'); setCurrency('INR'); }}
                    />
                    <div className="payment-label">
                      <span className="payment-title">Razorpay (UPI, Cards, NetBanking)</span>
                      <span className="payment-icons">🇮🇳 Best for India</span>
                    </div>
                  </div>

                  {/* Stripe Option */}
                  <div className={`payment-option-radio ${paymentMethod === 'stripe' ? 'selected' : ''}`}
                    onClick={() => { setPaymentMethod('stripe'); setCurrency('USD'); }}>
                    <input
                      type="radio"
                      checked={paymentMethod === 'stripe'}
                      onChange={() => { setPaymentMethod('stripe'); setCurrency('USD'); }}
                    />
                    <div className="payment-label">
                      <span className="payment-title">Credit / Debit Card (Stripe)</span>
                      <span className="payment-icons">💳 International Cards</span>
                    </div>
                  </div>

                  {paymentMethod === 'stripe' && (
                    <div className="stripe-element-container">
                      <CardElement options={{
                        style: {
                          base: { fontSize: '16px', color: '#424770', '::placeholder': { color: '#aab7c4' } },
                          invalid: { color: '#9e2146' },
                        },
                      }} />
                    </div>
                  )}

                  {/* PayPal Option */}
                  <div className={`payment-option-radio ${paymentMethod === 'paypal' ? 'selected' : ''}`}
                    onClick={() => { setPaymentMethod('paypal'); setCurrency('USD'); }}>
                    <input
                      type="radio"
                      checked={paymentMethod === 'paypal'}
                      onChange={() => { setPaymentMethod('paypal'); setCurrency('USD'); }}
                    />
                    <div className="payment-label">
                      <span className="payment-title">PayPal</span>
                      <span className="payment-icons">🌏 Global</span>
                    </div>
                  </div>

                  {paymentMethod === 'paypal' && (
                    <div className="paypal-container">
                      <PayPalButtons
                        createOrder={async () => {
                          try {
                            const orderId = `ORDER_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                            return orderId;
                          } catch (error) {
                            console.error(error);
                            return null;
                          }
                        }}
                        onApprove={async (data, actions) => {
                          toast.success('PayPal payment simulated successfully!');
                          clearCart();
                          navigate('/order-success', {
                            state: {
                              orderId: data.orderID,
                              paymentId: 'PAYPAL_SIM_' + Date.now(),
                              amount: total,
                              currency: currency,
                              paymentMethod: 'PayPal',
                              items: cartItems
                            }
                          });
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Step 3: Review Items */}
          <div className="checkout-step">
            <div className="step-header">
              <h3>3. Items and Delivery</h3>
            </div>
            <div className="step-content">
              {cartItems.map(item => (
                <div key={item.id} className="review-item">
                  <img src={item.image} alt={item.name} className="review-img" />
                  <div className="review-details">
                    <h4>{item.name}</h4>
                    <p>Qty: {item.quantity}</p>
                    <p className="price">₹{(item.price * item.quantity).toLocaleString('en-IN')}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* Right Sidebar: Order Summary */}
        <div className="checkout-sidebar">
          <div className="order-summary-box">
            {paymentMethod !== 'paypal' && (
              <button
                className="place-order-btn"
                onClick={handlePayment}
                disabled={!addressConfirmed || isProcessing || !deliveryOptions?.available}
              >
                {isProcessing ? 'Processing...' : !deliveryOptions?.available ? 'Delivery Not Available' : 'Place Your Order'}
              </button>
            )}

            <div className="summary-terms">
              By placing your order, you agree to India Crafts Store's privacy notice and conditions of use.
            </div>

            <div className="summary-line-items">
              <h3>Order Summary</h3>
              <div className="summary-row">
                <span>Items:</span>
                <span>₹{subtotal.toLocaleString('en-IN')}</span>
              </div>
              <div className="summary-row">
                <span>Delivery:</span>
                <span>
                  {!addressConfirmed ? (
                    <span style={{ color: '#888', fontSize: '0.9em' }}>Enter address first</span>
                  ) : loadingDelivery ? (
                    <span style={{ color: '#888', fontSize: '0.9em' }}>Checking...</span>
                  ) : deliveryOptions?.available ? (
                    shippingCost === 0 ? 'Free' : `₹${shippingCost}`
                  ) : (
                    <span style={{ color: '#888', fontSize: '0.9em' }}>--</span>
                  )}
                </span>
              </div>
              {appliedcoupon && discountAmount > 0 && (
                <div className="summary-row discount">
                  <span>coupon ({appliedcoupon.code}):</span>
                  <span>-₹{discountAmount.toLocaleString('en-IN')}</span>
                </div>
              )}
              <div className="summary-row total">
                <span>Order Total:</span>
                <span>₹{total.toLocaleString('en-IN')}</span>
              </div>
            </div>

            <div className="coupon-code-section">
              {appliedcoupon ? (
                <div className="applied-coupon-checkout">
                  <span>✅ {appliedcoupon.code} applied</span>
                  <button onClick={handleRemovecoupon} className="remove-coupon-btn">Remove</button>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    placeholder="Enter offer/coupon code"
                    value={couponCode}
                    onChange={(e) => setcouponCode(e.target.value.toUpperCase())}
                  />
                  <button onClick={handleApplycoupon} disabled={!couponCode || applyingcoupon}>
                    {applyingcoupon ? 'Applying...' : 'Apply'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Checkout Component with Stripe Elements and PayPal Script wrapper
const Checkout = () => {
  const paypalOptions = {
    'client-id': process.env.REACT_APP_PAYPAL_CLIENT_ID || 'test',
    currency: 'USD',
    intent: 'capture',
    'enable-funding': 'paypal',
    'disable-funding': 'paylater,card'
  };

  return (
    <PayPalScriptProvider options={paypalOptions}>
      <Elements stripe={stripePromise}>
        <CheckoutForm />
      </Elements>
    </PayPalScriptProvider>
  );
};

export default Checkout;
